// src/pages/admin/CustomerManagementPage.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, TextField, Switch, FormControlLabel, Alert, Paper } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';
import CustomerHistoryModal from './CustomerHistoryModal.js'; // Asegúrate de que la ruta sea correcta

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 500 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const CustomerManagementPage = () => {
    // --- ESTADOS ---
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false); // Para el modal de Crear/Editar
    const [formData, setFormData] = useState({});
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [error, setError] = useState('');

    // Estados para el modal de historial
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // --- CARGA DE DATOS ---
    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/customers?timestamp=${new Date().getTime()}`);
            setCustomers(response.data);
        } catch (err) {
            console.error("Error al obtener clientes:", err);
            setError("No se pudieron cargar los clientes.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    // --- MANEJADORES DE MODALES Y FORMULARIO ---
    const handleOpenCreate = () => {
        setEditingCustomer(null);
        setFormData({ name: '', email: '', phone: '', credit_limit: 0, is_credit_authorized: false });
        setError('');
        setOpenModal(true);
    };

    const handleOpenEdit = (customer) => {
        setEditingCustomer(customer);
        setFormData({
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
            credit_limit: customer.credit_limit || 0,
            is_credit_authorized: customer.is_credit_authorized || false
        });
        setError('');
        setOpenModal(true);
    };

    const handleOpenHistory = (customer) => {
        setSelectedCustomer(customer);
        setHistoryModalOpen(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setHistoryModalOpen(false);
    };
    
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        setError('');
        try {
            if (editingCustomer) {
                await api.put(`/customers/${editingCustomer.id}`, formData);
            } else {
                await api.post('/customers', formData);
            }
            handleCloseModal();
            fetchCustomers();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar el cliente.');
        }
    };

    // --- DEFINICIÓN DE COLUMNAS (CORREGIDA) ---
    const columns = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'name', headerName: 'Nombre', width: 250 },
        { field: 'email', headerName: 'Email', width: 250 },
        {
            field: 'credit_balance', headerName: 'Saldo Deudor', type: 'number', width: 150,
            valueFormatter: (value) => value == null ? '' : `$${Number(value).toFixed(2)}`
        },
        {
            field: 'credit_limit', headerName: 'Límite de Crédito', type: 'number', width: 150,
            valueFormatter: (value) => value == null ? '' : `$${Number(value).toFixed(2)}`
        },
        { field: 'is_credit_authorized', headerName: 'Crédito Autorizado', type: 'boolean', width: 180 },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 250,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleOpenEdit(params.row)}>Editar</Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => handleOpenHistory(params.row)}>Historial</Button>
                </Box>
            )
        }
    ];

    // --- RENDERIZADO ---
    return (
        <Box sx={{ height: '85vh', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Gestión de Clientes</Typography>
                <Button variant="contained" onClick={handleOpenCreate}>Añadir Cliente</Button>
            </Box>
            
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <DataGrid
                rows={customers}
                columns={columns}
                loading={loading}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            />

            {/* Modal de Crear/Editar Cliente */}
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={style}>
                    <Typography variant="h6">{editingCustomer ? 'Editar Cliente' : 'Crear Cliente'}</Typography>
                    {/* El 'error' del submit se podría mostrar aquí si se mueve el estado 'error' a un objeto */}
                    <TextField margin="normal" required fullWidth label="Nombre" name="name" value={formData.name || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Email" name="email" value={formData.email || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Teléfono" name="phone" value={formData.phone || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth label="Límite de Crédito" name="credit_limit" type="number" value={formData.credit_limit || ''} onChange={handleInputChange} />
                    <FormControlLabel
                        control={<Switch checked={!!formData.is_credit_authorized} onChange={handleInputChange} name="is_credit_authorized" />}
                        label="Autorizar Crédito"
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Modal de Historial de Cliente */}
            {historyModalOpen && (
                <CustomerHistoryModal
                    open={historyModalOpen}
                    onClose={handleCloseModal}
                    customer={selectedCustomer}
                />
            )}
        </Box>
    );
};

export default CustomerManagementPage;